/**
 * Company Service
 * 
 * Handles all company-related API operations including:
 * - CRUD operations for companies
 * - Integration configuration management
 * - Connection testing
 * - Sync operations
 */

import { apiClient } from './apiClient';
import type {
  Company,
  CompanyListItem,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  UpdateIntegrationRequest,
  TestConnectionResponse,
  SyncResult,
  SyncHistoryEntry,
  CompanyStatistics,
} from '@/types';

const BASE_URL = '/companies';

/**
 * Get all companies in the organisation
 */
export async function getCompanies(): Promise<CompanyListItem[]> {
  return apiClient.get<CompanyListItem[]>(BASE_URL);
}

/**
 * Get a specific company by ID
 */
export async function getCompany(companyId: string): Promise<Company> {
  return apiClient.get<Company>(`${BASE_URL}/${companyId}`);
}

/**
 * Create a new company
 */
export async function createCompany(request: CreateCompanyRequest): Promise<Company> {
  return apiClient.post<Company, CreateCompanyRequest>(BASE_URL, request);
}

/**
 * Update an existing company
 */
export async function updateCompany(
  companyId: string,
  request: UpdateCompanyRequest
): Promise<Company> {
  return apiClient.put<Company, UpdateCompanyRequest>(`${BASE_URL}/${companyId}`, request);
}

/**
 * Delete a company (soft delete)
 */
export async function deleteCompany(companyId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${companyId}`);
}

/**
 * Get company statistics
 */
export async function getCompanyStatistics(companyId: string): Promise<CompanyStatistics> {
  return apiClient.get<CompanyStatistics>(`${BASE_URL}/${companyId}/statistics`);
}

/**
 * Update Financial integration configuration
 */
export async function updateFinancialIntegration(
  companyId: string,
  request: UpdateIntegrationRequest
): Promise<Company> {
  return apiClient.put<Company, UpdateIntegrationRequest>(
    `${BASE_URL}/${companyId}/integrations/financial`,
    request
  );
}

/**
 * Update HR integration configuration
 */
export async function updateHRIntegration(
  companyId: string,
  request: UpdateIntegrationRequest
): Promise<Company> {
  return apiClient.put<Company, UpdateIntegrationRequest>(
    `${BASE_URL}/${companyId}/integrations/hr`,
    request
  );
}

/**
 * Test Financial integration connection
 */
export async function testFinancialConnection(
  companyId: string
): Promise<TestConnectionResponse> {
  return apiClient.post<TestConnectionResponse, Record<string, never>>(
    `${BASE_URL}/${companyId}/integrations/financial/test`,
    {}
  );
}

/**
 * Test HR integration connection
 */
export async function testHRConnection(companyId: string): Promise<TestConnectionResponse> {
  return apiClient.post<TestConnectionResponse, Record<string, never>>(
    `${BASE_URL}/${companyId}/integrations/hr/test`,
    {}
  );
}

/**
 * Disconnect Financial integration
 */
export async function disconnectFinancialIntegration(companyId: string): Promise<Company> {
  return apiClient.delete<Company>(`${BASE_URL}/${companyId}/integrations/financial`);
}

/**
 * Disconnect HR integration
 */
export async function disconnectHRIntegration(companyId: string): Promise<Company> {
  return apiClient.delete<Company>(`${BASE_URL}/${companyId}/integrations/hr`);
}

/**
 * Trigger manual sync for Financial integration
 */
export async function syncFinancialData(companyId: string): Promise<SyncResult> {
  return apiClient.post<SyncResult, Record<string, never>>(
    `${BASE_URL}/${companyId}/sync/financial`,
    {}
  );
}

/**
 * Trigger manual sync for HR integration
 */
export async function syncHRData(companyId: string): Promise<SyncResult> {
  return apiClient.post<SyncResult, Record<string, never>>(
    `${BASE_URL}/${companyId}/sync/hr`,
    {}
  );
}

/**
 * Get sync history for a company
 */
export async function getSyncHistory(
  companyId: string,
  integrationType?: 'financial' | 'hr',
  limit: number = 50
): Promise<SyncHistoryEntry[]> {
  const params = new URLSearchParams();
  if (integrationType) {
    params.append('type', integrationType);
  }
  params.append('limit', limit.toString());

  return apiClient.get<SyncHistoryEntry[]>(
    `${BASE_URL}/${companyId}/sync/history?${params.toString()}`
  );
}

/**
 * Validate company code uniqueness
 */
export async function validateCompanyCode(code: string, excludeId?: string): Promise<boolean> {
  const params = new URLSearchParams({ code });
  if (excludeId) {
    params.append('excludeId', excludeId);
  }

  const response = await apiClient.get<{ isAvailable: boolean }>(
    `${BASE_URL}/validate-code?${params.toString()}`
  );
  return response.isAvailable;
}

/**
 * Get integration configuration (without sensitive data)
 */
export async function getIntegrationConfig(
  companyId: string,
  integrationType: 'financial' | 'hr'
): Promise<Company['financialIntegration'] | Company['hrIntegration']> {
  return apiClient.get<Company['financialIntegration'] | Company['hrIntegration']>(
    `${BASE_URL}/${companyId}/integrations/${integrationType}`
  );
}
